import React, { useState, useEffect } from 'react';
import 'dirham/css';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import TagManager from 'react-gtm-module';
import { onAuthStateChanged } from 'firebase/auth';
import { auth } from './firebase/config/config';
import { ChatbotProvider } from './context/ChatbotContext';
import { UserProvider } from './context/UserContext';
import SubscriptionGuard from './components/SubscriptionGuard';
import ScrollToTop from './components/ScrollToTop';
import { initAffiliateTracker } from './utils/affiliateTracker';


// import Layout from './components/Layout';
import Platform from './pages/Platform/Platform';
import EmbedChatbot from './pages/Embed/EmbedChatbot';
import Login from './pages/Auth/Login';
import GetStarted from './pages/Auth/GetStarted';
import PaymentSuccess from './pages/Auth/PaymentSuccess';
import PaymentCancel from './pages/Auth/PaymentCancel';
import PrivacyPolicy from './pages/Home/Legal/PrivacyPolicy';
import TermsAndConditions from './pages/Home/Legal/TermsAndConditions';
import CancellationRefund from './pages/Home/Legal/CancellationRefund';
import ContactUs from './pages/Home/Legal/ContactUs';
import LegalHub from './pages/Home/Legal/LegalHub';
import FreeDemoPolicy from './pages/Home/Legal/FreeDemoPolicy';
import AffiliatePortal from './pages/Platform/Affiliates/AffiliatePortal';
import AffiliateManager from './pages/Platform/Admin/AffiliateManager';
import LanderPage from './pages/Lander/LanderPage';
import LanderResearchPage from './pages/Lander/LanderResearchPage';
import HomeStory from './pages/Home/HomeStory';
import FeaturesPage from './pages/Home/Features/FeaturesPage';
import PricingPage from './pages/Home/Pricing/PricingPage';
import BlogIndex from './pages/Home/Blog/BlogIndex';
import BlogPost from './pages/Home/Blog/BlogPost';
import EducationPage from './pages/Home/Niches/EducationPage';
import Phase2Landing from './pages/phase_2_landing/Phase2Landing';
import LandingPage from './pages/Landing/LandingPage';
import LaunchDealPage from './pages/Home/LaunchDeal/LaunchDealPage';
import GuidesPage from './pages/Home/Guides/GuidesPage';
import LLMInfoPage from './pages/Home/LLMInfo/LLMInfoPage';
import Navigation from './pages/Home/components/Navigation';
import Footer from './pages/Landing/footer';
// import ChatbotButton from './pages/Embed/ChatbotButton';


// import HolidayBanner from './components/HolidayBanner';

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const tagManagerArgs = { gtmId: 'GTM-NFHN2MVM' };
    TagManager.initialize(tagManagerArgs);
    initAffiliateTracker();
  }, []);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (u) => {
      setUser(u);
      setLoading(false);
    });
    return () => unsubscribe();
  }, []);

  if (loading) {
    return <div>Loading...</div>;
  }

  const withPublicChrome = (page) => (
    <>
      <Navigation />
      <main>{page}</main>
      <Footer />
    </>
  );

  return (
    <UserProvider>
      <ChatbotProvider>
        <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
          <ScrollToTop />
          {/* <HolidayBanner /> */}
          <Routes>
            <Route path="/" element={<LanderPage />} />
            <Route path="/features" element={<FeaturesPage />} />
            <Route path="/research" element={<LanderResearchPage />} />
            <Route path="/lander/research" element={<Navigate to="/research" replace />} />
            <Route path="/pricing" element={<PricingPage />} />
            <Route path="/launch-deal" element={<LaunchDealPage />} />
            <Route path="/blog" element={<BlogIndex />} />
            <Route path="/blog/:slug" element={<BlogPost />} />
            <Route path="/education" element={<EducationPage />} />
            <Route path="/landingv1" element={<LandingPage />} />
            <Route path="/landingv2" element={<Phase2Landing />} />
            <Route path="/landingv3" element={<HomeStory />} />
            <Route
              path="/login"
              element={user ? <Navigate to="/dashboard" /> : withPublicChrome(<Login />)}
            />
            <Route
              path="/get-started"
              element={withPublicChrome(<GetStarted />)}
            />
            <Route
              path="/payment/success"
              element={withPublicChrome(<PaymentSuccess />)}
            />
            <Route
              path="/payment/cancel"
              element={withPublicChrome(<PaymentCancel />)}
            />
            <Route
              path="/overview"
              element={user ? <SubscriptionGuard><Platform /></SubscriptionGuard> : <Navigate to="/login" />}
            />
            <Route
              path="/inbox/*"
              element={user ? <SubscriptionGuard><Platform /></SubscriptionGuard> : <Navigate to="/login" />}
            />
            <Route
              path="/analytics"
              element={user ? <SubscriptionGuard><Platform /></SubscriptionGuard> : <Navigate to="/login" />}
            />
            <Route
              path="/pro-settings"
              element={user ? <SubscriptionGuard><Platform /></SubscriptionGuard> : <Navigate to="/login" />}
            />
            <Route
              path="/chatbot/*"
              element={user ? <SubscriptionGuard><Platform /></SubscriptionGuard> : <Navigate to="/login" />}
            />
            <Route
              path="/admin/impersonation"
              element={user ? <SubscriptionGuard><Platform /></SubscriptionGuard> : <Navigate to="/login" />}
            />
            <Route path="/affiliates" element={withPublicChrome(<AffiliatePortal />)} />
            <Route path="/affiliate" element={<Navigate to="/affiliates" replace />} />
            <Route
              path="/admin/affiliates"
              element={user ? <SubscriptionGuard><AffiliateManager /></SubscriptionGuard> : <Navigate to="/login" />}
            />

            {/* Legacy Dashboard Redirect */}
            <Route path="/dashboard" element={<Navigate to="/overview" replace />} />
            <Route path="/embed/chatbot" element={<EmbedChatbot />} />
            {/* <Route path="/embed/chatbot-button" element={<ChatbotButton />} /> */}


            {/* Legal Pages - Accessible without login */}
            <Route path="/guides" element={<GuidesPage />} />
            <Route path="/llm-info" element={<LLMInfoPage />} />
            <Route path="/legal" element={<LegalHub />} />
            <Route path="/legal/privacy-policy" element={<PrivacyPolicy />} />
            <Route path="/legal/terms-conditions" element={<TermsAndConditions />} />
            <Route path="/legal/cancellation-refund" element={<CancellationRefund />} />
            <Route path="/legal/contact-us" element={<ContactUs />} />
            <Route path="/legal/free-demo-policy" element={<FreeDemoPolicy />} />
            <Route path="/free-demo-policy" element={<Navigate to="/legal/free-demo-policy" replace />} />

            <Route path="*" element={<Navigate to="/" />} />
          </Routes>
        </BrowserRouter>
      </ChatbotProvider>
    </UserProvider>
  );
}

export default App;
